<template>
  <div class="preview-pane">
    <!-- Preview hint -->
    <div class="px-4 py-2 bg-surface-sunken border-b" style="border-color: var(--border-muted);">
      <p class="text-caption" style="color: var(--text-secondary);">
        This preview uses your current header/footer templates with live data
      </p>
    </div>

    <!-- Preview error alert -->
    <div
      v-if="hasError"
      class="px-4 py-3">
      <div class="alert alert-warning flex items-center justify-between">
        <div class="flex items-center gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>Preview failed to load</span>
        </div>
        <button
          type="button"
          class="btn btn-secondary btn-sm"
          @click="refresh">
          Retry
        </button>
      </div>
    </div>

    <!-- Preview Container -->
    <div class="preview-wrapper flex justify-center mx-4 mb-4">
      <div
        class="preview-container relative bg-surface-sunken border rounded-lg overflow-hidden transition-all duration-300"
        :style="containerStyle">
        <!-- Loading overlay -->
        <div
          v-if="isLoading"
          class="preview-loading absolute inset-0 bg-white/90 flex items-center justify-center z-10">
          <div class="text-center">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2" />
            <p class="text-caption" style="color: var(--text-secondary);">
              Loading preview...
            </p>
          </div>
        </div>

        <!-- Preview iframe -->
        <iframe
          ref="previewIframe"
          class="w-full border-0 bg-white block"
          :style="iframeStyle"
          sandbox="allow-same-origin allow-scripts"
          title="Header & Footer Preview" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { usePreview } from './composables/usePreview'

const props = defineProps({
  headerContent: { type: Object, required: true },
  footerContent: { type: Object, required: true },
  headerSettings: { type: Object, default: () => ({}) },
  footerSettings: { type: Object, default: () => ({}) },
  headerTemplate: { type: String, default: 'navbar_service' },
  footerTemplate: { type: String, default: 'footer_columns' },
  previewMode: { type: String, default: 'desktop' },
  apiEndpoints: { type: Object, required: true }
})

// Setup preview composable
const preview = usePreview(props.apiEndpoints)
const previewIframe = ref(null)

// Computed
const isLoading = computed(() => preview.isLoading.value)
const hasError = computed(() => !!preview.error.value)

const containerStyle = computed(() => ({
  borderColor: 'var(--border-muted)',
  width: props.previewMode === 'mobile' ? '375px' : '100%'
}))

const iframeStyle = computed(() => ({
  height: '500px',
  minHeight: '400px'
}))

// Update iframe content
function updateIframe() {
  if (!previewIframe.value) return

  const doc = previewIframe.value.contentDocument || previewIframe.value.contentWindow?.document
  if (!doc) return

  const html = preview.generatePreviewDocument()
  doc.open()
  doc.write(html)
  doc.close()
}

// Fetch previews and update iframe
async function refresh() {
  await preview.fetchPreviews(
    {
      content: props.headerContent,
      settings: props.headerSettings,
      template: props.headerTemplate
    },
    {
      content: props.footerContent,
      settings: props.footerSettings,
      template: props.footerTemplate
    }
  )

  await nextTick()
  updateIframe()
}

// Debounced refresh
function debouncedRefresh() {
  preview.debouncedFetchPreviews(
    {
      content: props.headerContent,
      settings: props.headerSettings,
      template: props.headerTemplate
    },
    {
      content: props.footerContent,
      settings: props.footerSettings,
      template: props.footerTemplate
    }
  )
}

// Watch for content changes
watch(
  () => [props.headerContent, props.headerSettings, props.headerTemplate],
  () => {
    debouncedRefresh()
  },
  { deep: true }
)

watch(
  () => [props.footerContent, props.footerSettings, props.footerTemplate],
  () => {
    debouncedRefresh()
  },
  { deep: true }
)

// Watch for preview HTML changes to update iframe
watch(
  () => [preview.headerHtml.value, preview.footerHtml.value],
  () => {
    nextTick(() => updateIframe())
  }
)

// Initial load
onMounted(() => {
  refresh()
})

// Expose refresh method for parent component
defineExpose({ refresh })
</script>

<style scoped>
.preview-pane {
  min-height: 400px;
}

.preview-container {
  transition: width 0.3s ease;
}
</style>
